import { SCHEMA_CONTEXT, COMPANY_INFO, SOFTWARE_APP_INFO } from './constants';
import type {
  WebSiteSchema,
  WebPageSchema,
  BreadcrumbListSchema,
  SoftwareApplicationSchema,
  BlogPostingSchema,
  FAQPageSchema,
  SchemaOptions
} from './types';

// Generate WebSite schema (for homepage)
export function generateWebSiteSchema(options: Pick<SchemaOptions, 'pageName' | 'pageUrl' | 'keywords'>): WebSiteSchema {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "WebSite",
    name: COMPANY_INFO.name,
    alternateName: options.pageName,
    url: options.pageUrl,
    keywords: options.keywords || []
  };
}

// Generate WebPage schema (for all pages)
export function generateWebPageSchema(options: Pick<SchemaOptions, 'pageName' | 'pageDescription' | 'pageUrl' | 'keywords' | 'breadcrumbs' | 'speakable'>): WebPageSchema {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "WebPage",
    name: options.pageName,
    alternateName: options.pageDescription,
    description: options.pageDescription,
    url: options.pageUrl,
    keywords: options.keywords || [],
    ...(options.breadcrumbs && options.breadcrumbs.length > 0 && {
      breadcrumb: {
        "@type": "BreadcrumbList" as const,
        itemListElement: options.breadcrumbs.map((crumb, index) => ({
          "@type": "ListItem" as const,
          position: index + 1,
          name: crumb.name,
          item: crumb.url,
        })),
      },
    }),
    ...(options.speakable && options.speakable.length > 0 && {
      speakable: {
        "@type": "SpeakableSpecification" as const,
        cssSelector: options.speakable,
      },
    }),
  };
}

// Generate BreadcrumbList schema
export function generateBreadcrumbListSchema(breadcrumbs: Array<{ name: string; url: string }>): BreadcrumbListSchema {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.url
    }))
  };
}

// Generate SoftwareApplication schema (for homepage or custom pages)
export function generateSoftwareApplicationSchema(customInfo?: {
  name?: string;
  operatingSystem?: string;
  applicationCategory?: string;
  aggregateRating?: {
    ratingValue: number;
    ratingCount: number;
  } | null;
  offers?: {
    price: number;
    priceCurrency: string;
  };
}): SoftwareApplicationSchema {
  // Merge custom info with defaults from SOFTWARE_APP_INFO
  const info = customInfo || {};

  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "SoftwareApplication",
    name: info.name || SOFTWARE_APP_INFO.name,
    operatingSystem: info.operatingSystem || SOFTWARE_APP_INFO.operatingSystem,
    applicationCategory: info.applicationCategory || SOFTWARE_APP_INFO.applicationCategory,
    // Only emit AggregateRating when a page passes one explicitly. Do not fall back to a default.
    ...(info.aggregateRating
      ? {
          aggregateRating: {
            "@type": "AggregateRating" as const,
            ratingValue: info.aggregateRating.ratingValue,
            ratingCount: info.aggregateRating.ratingCount,
          },
        }
      : {}),
    offers: info.offers ? {
      "@type": "Offer",
      price: info.offers.price,
      priceCurrency: info.offers.priceCurrency
    } : SOFTWARE_APP_INFO.offers
  };
}

// Generate BlogPosting schema
export function generateBlogPostingSchema(options: Required<Pick<SchemaOptions, 'pageUrl' | 'blogPost'>>): BlogPostingSchema {
  const { pageUrl, blogPost } = options;

  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "BlogPosting",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": pageUrl
    },
    headline: blogPost.headline,
    alternativeHeadline: blogPost.headline,
    description: blogPost.description,
    articleBody: blogPost.articleBody,
    image: blogPost.image,
    genre: blogPost.genre || "Digital Signage",
    keywords: [],
    datePublished: blogPost.datePublished,
    dateModified: blogPost.dateModified,
    author: {
      "@type": "Person",
      name: blogPost.author
    },
    publisher: {
      "@type": "Organization",
      name: COMPANY_INFO.name,
      logo: {
        "@type": "ImageObject",
        url: COMPANY_INFO.logo
      }
    }
  };
}

// Generate FAQPage schema
export function generateFAQPageSchema(faqs: Array<{ question: string; answer: string }>): FAQPageSchema {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "FAQPage",
    mainEntity: faqs.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer
      }
    }))
  };
}

// Generate all schemas for a page based on type
export function generatePageSchemas(options: SchemaOptions) {
  const schemas = [];

  // Always include WebPage schema (Organization is emitted once by the root layout)
  schemas.push(generateWebPageSchema(options));

  // Add breadcrumbs if provided
  if (options.breadcrumbs && options.breadcrumbs.length > 0) {
    schemas.push(generateBreadcrumbListSchema(options.breadcrumbs));
  }

  // Add blog posting schema if blog post data is provided
  if (options.blogPost) {
    schemas.push(generateBlogPostingSchema({ pageUrl: options.pageUrl, blogPost: options.blogPost }));
  }

  // Add SoftwareApplication schema if requested
  if (options.includeSoftwareSchema) {
    schemas.push(generateSoftwareApplicationSchema(options.softwareApplication));
  }

  // Add FAQ schema if FAQs are provided
  if (options.faqs && options.faqs.length > 0) {
    schemas.push(generateFAQPageSchema(options.faqs));
  }
  return schemas;
}

// Generate homepage-specific schemas
export function generateHomepageSchemas(options: Pick<SchemaOptions, 'pageName' | 'pageDescription' | 'pageUrl' | 'keywords' | 'faqs'>) {
  const schemas = [];

  // Homepage-specific schemas
  schemas.push(generateWebSiteSchema(options));
  schemas.push(generateSoftwareApplicationSchema());

  // Standard schemas
  schemas.push(generateWebPageSchema(options));

  // Add FAQ schema if provided
  if (options.faqs && options.faqs.length > 0) {
    schemas.push(generateFAQPageSchema(options.faqs));
  }

  return schemas;
}
